function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) {
    elementToFocus.focus();
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }
  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        if (document.querySelector('.menu-drawer__searchbox') != null) {
          document.querySelector('.menu-drawer__searchbox').style.display = "none";
        }
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
    if (document.querySelector('.menu-drawer__searchbox') != null) {
      document.querySelector('.menu-drawer__searchbox').style.display = "block";
    }
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.getElementById('shopify-section-header');
    this.borderOffset = this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);
    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentPage - 1]
        }
      }));
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }  }

  focusInHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
``` 
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();

    this.slider.scrollTo({
      left: slideScrollPosition
    });
```  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length) linkElements.forEach(button => {
          button.removeAttribute('tabindex');
        });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length) linkElements.forEach(button => {
          button.setAttribute('tabindex', '-1');
        });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.productData = JSON.parse(document.getElementById('ProductData').textContent);
    this.productVariants = this.productData.variants;
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      console.log("Hello now");
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    const variant = this.productVariants.find((variant) => variant.title === this.selectedOptionValuesName);
    let variantImage = '';
    console.log(this.selectedOptionValues);
    if (variant && variant.featured_image) {
      const featured_image = variant.featured_image.src;
      variantImage = variant.featured_image.src;
    }

    if (variantImage) {
      const mediaItem = document.querySelector(`.media-thumbnail[data-image="${variantImage}"]`);
      // const mediaGallery = mediaItem.closest('.media-gallery-thumbnails');
      if (mediaItem) {
        mediaItem.click();
      }
    } else {
      const mediaItem = document.querySelector('.media-thumbnail');
      if (mediaItem) mediaItem.click();
    }

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }

  get selectedOptionValuesName() {
    const optionsTitleArray =  Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ value }) => value
    );
    return optionsTitleArray.join(' / ');
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductSlider {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('.product-slider__wrapper');
    this.slides = container.querySelectorAll('.product-slider__slide');
    this.prevButton = container.querySelector('.slider-button--prev');
    this.nextButton = container.querySelector('.slider-button--next');
    this.progressBar = container.querySelector('.slider-line__progress');
    this.slidesPerView = this.slidesView(window.innerWidth);
    this.classList = this.slider.classList;
    this.config = {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: false,
      groupCells: true,
      draggable: true,
    }

    if (this.classList.contains('watch-css')) {
      this.config.watchCSS = true;
    }

    if (this.classList.contains('has-prev-next')) {
      this.config.prevNextButtons = true;
    }

    if (this.classList.contains('as-nav-for')) {
      this.config.asNavFor = this.slider.dataset.carouselId;
    }

    if (this.classList.contains('cell-align-center')) {
      this.config.cellAlign = 'center';
    }
    
    // Initialize Flickity
    this.flkty = new Flickity(this.slider, this.config);

    // Button click listeners
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.flkty.previous());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.flkty.next());
    }

    // Window resize event listener
    window.addEventListener('resize', () => {
      this.slidesPerView = this.slidesView(window.innerWidth);
      // this.flkty.options.groupCells = this.slidesPerView; // Update the grouping on resize
      this.flkty.reloadCells();
      this.flkty.resize(); // Trigger Flickity to recalculate
      this.setSlidesHeight(); // Adjust height on resize
    });

    // this.setSliderHeight();
    if (this.progressBar) this.updateProgressBar();

    if (this.prevButton && this.nextButton) this.updateButtonStates();

    // Flickity events
    if (this.progressBar) {
      this.flkty.on('scroll', () => this.updateProgressBar());
    }
    this.flkty.on('change', () => {
      if (this.prevButton && this.nextButton) this.updateButtonStates();
      this.setSliderHeight(); // Adjust height after slide change
    });
    
     // Apply height 100% to the slides after Flickity initialization
    this.setSlidesHeight();
  }

   setSlidesHeight() {
    // Ensure each slide has a height of 100% after the slider is initialized
    this.slides.forEach(slide => {
      slide.style.height = '100%';
    });
  }


  slidesView(width) {
    if (width < 768) {
      return 1;
    } else if (width >= 768 && width <= 991) {
      return 2;
    } else {
      return 4;
    }
  }

  updateProgressBar() {
      if (!this.flkty || !this.flkty.slides) {
          return;
      }
      const totalSlides = this.flkty.slides.length;
      const progress = ((this.flkty.selectedIndex + 1) / totalSlides) * 100;
      if (this.progressBar) {
          this.progressBar.style.width = `${progress}%`;
      }
  }

  updateButtonStates() {
    const totalSlides = this.flkty.slides.length;

    // Update previous button state
    if (this.flkty.selectedIndex === 0) {
      this.prevButton.classList.add('inactive');
    } else {
      this.prevButton.classList.remove('inactive');
    }
    console.log(totalSlides);
    console.log(this.flkty.selectedIndex);

    // Update next button state
    if (this.flkty.selectedIndex === totalSlides - 1) {
      this.nextButton.classList.add('inactive');
    } else {
      this.nextButton.classList.remove('inactive');
    }
  }
}


document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.product-slider');
  sliders.forEach(slider => new ProductSlider(slider));
});

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }


  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        const quickAddEl = this.querySelectorAll('.quick-add-variant');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
            const sliders = document.querySelectorAll('.product-slider');
            sliders.forEach(slider => new ProductSlider(slider));
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        if (quickAddEl) {
          quickAddEl.forEach(el => {
            el.addEventListener('change', e => {
              const target = e.currentTarget;
              const parent = target.closest('.card__content');
              // change variant id
              const prodIdEl = parent.querySelector('input[name="id"]');
              prodIdEl.value = target.value;
              // change price
              const selectedIndex = target.selectedIndex;
              const priceHTML = target.options[selectedIndex].dataset.priceHtml;
              const prodPriceEl = parent.querySelector('.price-item--regular');
              prodPriceEl.innerHTML = priceHTML;
            });  
          });
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  } 

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  
  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  
  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
// customElements.define('product-recommendations', ProductRecommendations);

const ingred_carousel = document.querySelector(".ingred-slider-desktop");
const ingred_carousel_mob = document.querySelector(".ingred-slider-mobile");


if (ingred_carousel != null) {
  const flkty = new Flickity(ingred_carousel, {
    cellAlign: "center",
    wrapAround: true,
    contain: true,
    prevNextButtons: true,
    pageDots: true,
    freeScroll: true
  });
}
if (ingred_carousel_mob != null) {
  const flktymob = new Flickity(ingred_carousel_mob, {
    cellAlign: "center",
    wrapAround: true,
    prevNextButtons: true,
    pageDots: true,
    freeScroll: true
  });
}


/* Custom Js */
Shopify.money_format = "${{amount}}";
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == 'string') { cents = cents.replace('.', ''); }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = (format || this.money_format);


  function defaultOption(opt, def) {
    return (typeof opt == 'undefined' ? def : opt);
  }


  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');


    if (isNaN(number) || number == null) { return 0; }


    number = (number / 100.0).toFixed(precision);


    var parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? (decimal + parts[1]) : '';


    return dollars + cents;
  }


  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }


  return formatString.replace(placeholderRegex, value);
};


/* Custom JS */
if (document.querySelector(".bundle_checkbox")) {

  var variantSelector = document.querySelectorAll('.variant-size-options input[type="radio"]');
  var bundleContainer12 = document.querySelector('.bundleContainer12');
  var bundleContainer24 = document.querySelector('.bundleContainer24');
  var Qtyminselect = 4;
  var currentContainer = bundleContainer12;

  // Iterate over each radio button and add the event listener
  variantSelector.forEach(function (radio) {
    radio.addEventListener("change", function (event) {
      if (event.target.getAttribute('value') == '12') {
        bundleContainer12.style.display = 'block';
        bundleContainer24.style.display = 'none';
        currentContainer = bundleContainer12;
      } else {
        currentContainer = bundleContainer24;
        bundleContainer12.style.display = 'none';
        bundleContainer24.style.display = 'block';
      }
      
      var bundles = document.querySelectorAll('.bundle_cls');
      bundles.forEach(function (bundle) {
        var qty = bundle.querySelector('.bundle_checkbox_cls[data-qty]').getAttribute('data-qty');
        for (let index = 0; index < qty; index++) {
          bundle.querySelector('.js-qty__adjust--minus').click();
        }
      });
    });
  });

  function setupQuantityAdjusters(bundleContainer) {
    var minus = bundleContainer.querySelectorAll(".js-qty__adjust--minus");
    var add = bundleContainer.querySelectorAll(".js-qty__adjust--plus");

    minus.forEach(function (e) {
      e.addEventListener("click", function (event) {
        var currentValue = event.target.parentNode.closest('.js-qty__wrapper').querySelector('.product-info-qty').value;
        if (currentValue > 0) {
          currentValue = parseInt(currentValue) - 1;
          event.target.parentNode.closest('.js-qty__wrapper').querySelector('.product-info-qty').value = currentValue;
          checkBoxValue(e, currentValue);
        }
      });
    });

    add.forEach(function (e) {
      e.addEventListener("click", function (event) {
        var currentValue = event.target.parentNode.closest('.js-qty__wrapper').querySelector('.product-info-qty').value;
        currentValue = parseInt(currentValue) + 1;
        event.target.parentNode.closest('.js-qty__wrapper').querySelector('.product-info-qty').value = currentValue;
        checkBoxValue(e, currentValue);
      });
    });
  }

  setupQuantityAdjusters(bundleContainer12);
  setupQuantityAdjusters(bundleContainer24);

  function qtymainValue(e) {
    let minValqty = e.target.closest('.js-qty__wrapper').querySelector('.product-info-qty').getAttribute('min');
    let FinalQtyval = e.target.closest('.js-qty__wrapper').querySelector('.product-info-qty').value;
    if (minValqty == FinalQtyval) {
      e.target.closest('.js-qty__wrapper').querySelector('.js-qty__adjust--minus').setAttribute("disabled", true);
    } else {
      e.target.closest('.js-qty__wrapper').querySelector('.js-qty__adjust--minus').removeAttribute("disabled");
    }
  }

  function qtyBoxchnageValue(e) {
    var bdFinalTotal = 0;
    var QtyFinal = 0;
    var checkboxes = e.target.closest('.bundle_checkbox').getElementsByClassName('bundle_checkbox_cls');
    if (e.target.parentNode.closest('.bundle_list_content')) {
      var selectedQty = e.target.parentNode.closest('.bundle_list_content').getElementsByClassName('product-info-qty')[0].value;
      var currentInput = e.currentTarget.closest(".bundle_check").getElementsByClassName('bundle_checkbox_cls')[0];
      currentInput.setAttribute("data-qty", selectedQty);
      var currentPrice = Number(currentInput.getAttribute("data-price"));
      var currentTotalPrice = currentPrice * selectedQty;
      currentInput.setAttribute("data-total-price", currentTotalPrice);
      for (i = 0; i < checkboxes.length; i++) {
        var qycheck = checkboxes[i].getAttribute("data-qty");
        if (qycheck > 0) {
          var itemTotalPrice = Number(checkboxes[i].getAttribute("data-total-price"));
          bdFinalTotal = bdFinalTotal + itemTotalPrice;
          var itemTotalQty = Number(checkboxes[i].getAttribute("data-qty"));
          QtyFinal = QtyFinal + itemTotalQty;
        }
      }
      var finalQty = Qtyminselect - QtyFinal;
      if (Qtyminselect > QtyFinal) {
        currentContainer.querySelector('.progress_bundle_bar').style.display = 'block';
        currentContainer.querySelector('.progress_bundle_bar').querySelector('span').textContent = finalQty;
        currentContainer.querySelector('.bb_bb_atc_btn_event').setAttribute('disabled', "disabled");
        currentContainer.querySelectorAll('.bundle_list_content').forEach((e) => {
          if (e.querySelector('.js-qty__adjust--plus')) {
            e.querySelector('.js-qty__adjust--plus').removeAttribute("disabled");
          }
        });
      } else {
        currentContainer.querySelector('.progress_bundle_bar').style.display = 'none';
        currentContainer.querySelector('.bb_bb_atc_btn_event').removeAttribute('disabled');
        currentContainer.querySelectorAll('.bundle_list_content').forEach((e) => {
          if (e.querySelector('.js-qty__adjust--plus')) {
            e.querySelector('.js-qty__adjust--plus').setAttribute("disabled", true);
          }
        });
      }

      if (Qtyminselect <= QtyFinal) {
        console.log('up or same');
      } else {
        console.log('down');
      }

      var parent = e.target.parentNode.closest('.bundle_check');
      var img = parent.querySelector('.bundle-pro-image');
      var qtySpan = parent.querySelector('#qty_checker');
      if (selectedQty > 0) {
        img.style.transform = 'translateY(-10%)';
        qtySpan.innerHTML = selectedQty;
        qtySpan.style.display = 'block';
      } else {
        img.style.transform = 'translateY(0)';
        qtySpan.style.display = 'none';
      }

      currentContainer.getElementsByClassName('bundle_total_price')[0].innerHTML = Shopify.formatMoney(bdFinalTotal);
      currentContainer.getElementsByClassName('bundle_total_price')[0].setAttribute('data-total-price', bdFinalTotal);
    }
  }

  function CheckedArray(checkboxes) {
    var $th_temPrice = 0;
    var $th_temComparePrice = 0;
    for (i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i].checked) {
        var itemPrice = Number(checkboxes[i].getAttribute("data-new-price"));
        var itemQty = Number(checkboxes[i].getAttribute("data-qty"));
        itemPrice = itemPrice * itemQty;
        var itemComparePrice = Number(checkboxes[i].getAttribute("data-compare-price"));
        $th_temPrice = $th_temPrice + itemPrice;
        $th_temComparePrice = $th_temComparePrice + itemComparePrice;
      }
    }
    var mainPriceCls = document.getElementsByClassName('bundle_total_price')[0];
    var comparePriceCls = document.getElementsByClassName('compare_bundle_price')[0];
    mainPriceCls.innerHTML = Shopify.formatMoney($th_temPrice);
    comparePriceCls.innerHTML = Shopify.formatMoney($th_temComparePrice);
    if ($th_temComparePrice > $th_temPrice) {
      comparePriceCls.classList.remove("hide");
    } else {
      comparePriceCls.classList.add("hide");
    }
  }

  function checkBoxValue(e, event) {
    if (e.closest('.bundle_list_content')) {
      if (event > 0) {
        e.closest('.bundle_list_content').querySelector('.bundle_checkbox_cls').checked = true;
      } else {
        e.closest('.bundle_list_content').querySelector('.bundle_checkbox_cls').checked = false;
      }
      var checkboxes = e.closest('.bundle_checkbox').getElementsByClassName('bundle_checkbox_cls');
      CheckedArray(checkboxes);
    }
  }

  function setupEventListeners(bundleContainer) {
    var qtySelectors = bundleContainer.getElementsByClassName('product-info-qty');
    for (var i = 0; i < qtySelectors.length; i++) {
      qtySelectors[i].addEventListener('input', qtyBoxchnageValue);
      qtySelectors[i].addEventListener('focusout', qtyBoxchnageValue);
    }

    var qtybtn = bundleContainer.querySelectorAll('.js-qty__adjust');
    for (var j = 0; j < qtybtn.length; j++) {
      qtybtn[j].addEventListener('click', qtymainValue);
      qtybtn[j].addEventListener('click', qtyBoxchnageValue);
    }
  }

  setupEventListeners(bundleContainer12);
  setupEventListeners(bundleContainer24);

  const $bb_bb_addtocart_event = async function (btn_this) {
    btn_this.querySelector('.button_cart_loader').classList.add('show');
    let $bb_bb_pbIdArray = [], $bb_bb_cart_response;
    const $bb_checkboxed = btn_this.closest('.bundle_prod_wrapp').querySelectorAll('.bundle_checkbox_cls');
    const $bb_mainqty = btn_this.closest('.bundle_prod_wrapp').querySelector('.wrap-bundle_act').querySelector('.product-info-qty').value;

    for (var i = 0; i < $bb_checkboxed.length; i++) {
      if ($bb_checkboxed[i].checked && $bb_checkboxed[i].getAttribute("data-qty") > 0) {
        $bb_bb_pbIdArray.push({
          id: Number($bb_checkboxed[i].value),
          quantity: Number($bb_checkboxed[i].getAttribute("data-qty")) * Number($bb_mainqty),
          sections: document.querySelector('cart-drawer').getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname
        });
      }
    }
    $bb_bb_pbIdArray.reverse();
    const $bb_bb_addtocart_fetch_event = async function () {
      if ($bb_bb_pbIdArray.length > 0) {
        let $bb_bb_pbRequest = $bb_bb_pbIdArray.shift();
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: $bb_bb_pbRequest.id, quantity: $bb_bb_pbRequest.quantity, sections: $bb_bb_pbRequest.sections, sections_url: $bb_bb_pbRequest.sections_url }),
        }).then((response) => response.json()).then((cart_response) => {
          $bb_bb_cart_response = cart_response;
          $bb_bb_addtocart_fetch_event();
        }).catch((error) => {
          console.error('Error: ', error);
          btn_this.querySelector('.button_cart_loader').classList.remove('show');
        });
      } else {
        const quickAddModal = document.querySelector('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => {
            setTimeout(() => {
              document.querySelector('cart-drawer').renderContents($bb_bb_cart_response);
            });
          }, { once: true });
          quickAddModal.hide(true);
        } else {
          document.querySelector('cart-drawer').renderContents($bb_bb_cart_response);
        }
        if (document.querySelector('cart-drawer') && document.querySelector('cart-drawer').classList.contains('is-empty')) document.querySelector('cart-drawer').classList.remove('is-empty');
        btn_this.querySelector('.button_cart_loader').classList.remove('show');
      }
    }
    $bb_bb_addtocart_fetch_event();
  }

  document.querySelectorAll('.bb_bb_atc_btn_event').forEach(function (atc_this, atc_idx) {
    atc_this.addEventListener('click', function (event) {
      event.preventDefault(), event.stopImmediatePropagation();
      $bb_bb_addtocart_event(atc_this);
    });
  });
}



document.querySelectorAll('.wrapper_cart__left .recommendation-atc').forEach(function ($ps_this, $idx) {
  $ps_this.addEventListener('click', function (event) {
    event.preventDefault(), event.stopImmediatePropagation();
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: this.dataset.vid, quantity: 1, sections: document.querySelector('cart-drawer').getSectionsToRender().map((section) => section.id), sections_url: window.location.pathname }),
    }).then((response) => response.json()).then((cart_response) => {
      const quickAddModal = document.querySelector('quick-add-modal');
      window.location = "/cart";
      if (document.querySelector('cart-drawer') && document.querySelector('cart-drawer').classList.contains('is-empty')) document.querySelector('cart-drawer').classList.remove('is-empty');
    }).catch((error) => {
      console.error('Error: ', error);
    });
  });
});

const recommendation_carousel = document.querySelector(".cart-drawer .scroller__inner .inner-cart-recommendation");
if (recommendation_carousel != null) {
  setTimeout(() => {
    const flkty = new Flickity(recommendation_carousel, {
      cellAlign: 'left',
      freeScroll: true,
      prevNextButtons: false,
      pageDots: false
    });
  }, 200);
}
if (document.querySelectorAll(".video-section__toggle") != null) {
  document.querySelectorAll(".video-section__toggle").forEach(function (event) {
    event.addEventListener("click", function (evnt) {
      let video_src = this.getAttribute('data-src');
      this.parentNode.classList.toggle("video_popup_open");
      if (document.querySelector(".video_popup_open")) {
        this.parentNode.querySelector('.video_popup iframe').setAttribute('src', video_src);
      } else {
        let video_close_src = video_src.replace('&autoplay=1', '&autoplay=0')
        this.parentNode.querySelector('.video_popup iframe').setAttribute('src', video_close_src);
      }
    });
  });
}
if (document.querySelectorAll(".close_popup .icon-close") != null) {
  document.querySelectorAll(".close_popup .icon-close").forEach(function (event) {
    event.addEventListener("click", function (evnt) {
      let video_close_src = evnt.target.parentNode.getAttribute('data-closesrc');
      if (document.querySelector(".video_popup_open")) {
        this.parentNode.parentNode.parentNode.querySelector('.video_popup iframe').setAttribute('src', video_close_src);
      }
      this.parentNode.parentNode.parentNode.classList.toggle("video_popup_open");
    });
  });
}